import * as Yup from 'yup';
import {
    MAX_RESERVATION_VESSEL_BEAM,
    MAX_RESERVATION_VESSEL_DRAFT,
    MAX_RESERVATION_VESSEL_LOA,
    MIN_RESERVATION_VESSEL_DIMENSION
} from "./config";
import {NatureOfTravel, HullType, VesselType} from "../settings/enums";

const nameRexp = /^[a-zA-Z][^{0-9}#&@<=>+_"(|)\\\/ˆ˜'*~.,;:$^%±§!?\[\]]+$/;
const phoneRexp = /^(\+{0,1}\d(\d|-){4,})$/;
const emailRexp =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const getInitialInquiryValidationSchema = (t, overrides = {}) => {
    const startDate = Yup.number().typeError(t('DATE_TYPE_ERROR')).required(t('DATE_TYPE_ERROR'))
    const endDate = Yup.number().typeError(t('DATE_TYPE_ERROR')).required(t('DATE_TYPE_ERROR'))

    // LOA
    const vesselLength = Yup.number()
        .typeError(t('NUMBER_TYPE_ERROR'))
        .min(MIN_RESERVATION_VESSEL_DIMENSION, t('VESSEL_LENGTH_ERROR'))
        .max(overrides.loa ?? MAX_RESERVATION_VESSEL_LOA, t('VESSEL_MAX_LENGTH_ERROR',
            {max_size: overrides.loa ?? MAX_RESERVATION_VESSEL_LOA}))
        .required(t('VESSEL_LOA_REQUIRED'));

    // Beam
    const vesselBeam = Yup.number()
        .typeError(t('NUMBER_TYPE_ERROR'))
        .min(MIN_RESERVATION_VESSEL_DIMENSION, t('VESSEL_BEAM_ERROR'))
        .max(overrides.beam ?? MAX_RESERVATION_VESSEL_BEAM, t('VESSEL_MAX_BEAM_ERROR',
            {max_size: overrides.beam ?? MAX_RESERVATION_VESSEL_BEAM}))
        .required(t('VESSEL_BEAM_REQUIRED'));

    // Draft
    const vesselDraft = Yup.number()
        .typeError(t('NUMBER_TYPE_ERROR'))
        .min(MIN_RESERVATION_VESSEL_DIMENSION, t('VESSEL_DRAFT_ERROR'))
        .max(overrides.draft ?? MAX_RESERVATION_VESSEL_DRAFT, t('VESSEL_MAX_DRAFT_ERROR',
            {max_size: overrides.draft ?? MAX_RESERVATION_VESSEL_DRAFT}))
        .required(t('VESSEL_DRAFT_REQUIRED'));

    // Vessel Type
    let vesselType = Yup.string().oneOf(Object.keys(VesselType));
    if (overrides.vesselType) vesselType = vesselType.required(t('VESSEL_TYPE_IS_REQUIRED'));

    // Hull Type
    let hullType = Yup.string().oneOf(Object.keys(HullType));
    if (overrides.hullType) hullType = hullType.required(t('VESSEL_HULL_TYPE_IS_REQUIRED'));

    // Nature of Travel
    let natureOfTravel = Yup.string().oneOf(Object.keys(NatureOfTravel));
    if (overrides.natureOfTravel) natureOfTravel = natureOfTravel.required(t('NATURE_OF_TRAVEL_REQUIRED'));

    let existingVesselName= Yup.string().nullable(true);
    if(overrides?.existingVesselName){
        existingVesselName = existingVesselName.required(t('BOAT_SELECT_REQUIRED'));
    }
    let homeMarinaId= Yup.string().nullable(true);
    if(overrides?.homeMarina) {
        homeMarinaId = homeMarinaId.required(t('HOME_MARINA_IS_REQUIRED'));
    }
    let country = Yup.string().nullable(true);
    if(overrides?.country) {
        country = country.required(t('COUNTRY_IS_REQUIRED'));
    }


    let contractId = Yup.string().typeError(t('SELECT_PERIOD_ERROR')).nullable();
    if(overrides.contract) contractId = Yup.string().typeError(t('SELECT_PERIOD_ERROR')).required(t('SELECT_PERIOD_ERROR'))

    return ({
        startDate,
        endDate,
        vesselLength,
        vesselBeam,
        vesselDraft,
        vesselType,
        hullType,
        natureOfTravel,
        contractId,
        existingVesselName,
        homeMarinaId,
        country
    })
}

export const getReservationValidationSchema = (t, requirements) => {
    const firstName = Yup.string().matches(nameRexp, t('FIRST_NAME_INCORRECT')).required(t('FIRST_NAME_REQUIRED'));
    const lastName = Yup.string().matches(nameRexp, t('LAST_NAME_INCORRECT')).required(t('LAST_NAME_REQUIRED'));
    const mail = Yup.string().email(t('EMAIL_INCORRECT')).matches(emailRexp, t('EMAIL_INCORRECT'))
        .required(t('EMAIL_IS_REQUIRED'));
    const mailConfirmation = Yup.string().required(t('EMAIL_CONFIRMATION_REQUIRED'))
        .email(t('EMAIL_INCORRECT'))
        .when('mail',{
        is: (val) => val !== '',
        then: schema => schema.oneOf([Yup.ref('mail')], t('EMAIL_CONFIRMATION_NO_MATCH'))
    });
    const vesselName = Yup.string().nullable(true).required(t('VESSEL_NAME_IS_REQUIRED'));
    const notes = Yup.string().max(500, t('NOTES_TOO_LONG'));

    const res = {
        firstName,
        lastName,
        mail,
        mailConfirmation,
        vesselName,
        notes
    }
    if(requirements?.existingVesselName){
        res.existingVesselName= Yup.string().nullable(true).required(t('BOAT_SELECT_REQUIRED'));
    }
    if(requirements?.phone) {
        res.phoneNumber = Yup.string().required(t('PHONE_NUMBER_REQUIRED')).matches(phoneRexp, t('PHONE_NUMBER_ERROR'));
    }
    if(requirements?.address) {
        res.address = Yup.string().required(t('ADDRESS_REQUIRED'));
    }
    if(requirements?.postCode) {
        res.postCode = Yup.string().required(t('POSTCODE_REQUIRED'));
    }
    if(requirements?.country) {
        res.country = Yup.string().nullable(true).required(t('COUNTRY_IS_REQUIRED'));
    }
    if(requirements?.vesselType) {
        res.vesselType = Yup.string().nullable(true).required(t('BOAT_TYPE_REQUIRED'));
    }
    if(requirements?.hullType) {
        res.hullType = Yup.string().nullable(true).required(t('HULL_TYPE_REQUIRED'));
    }
    if(requirements?.homeMarina) {
        res.homeMarinaId = Yup.string().nullable(true).required(t('HOME_MARINA_IS_REQUIRED'));
    }
    if(requirements?.natureOfTravel) {
        res.natureOfTravel = Yup.string().nullable(true).required(t('NATURE_OF_TRAVEL_REQUIRED'));
    }
    return res;
}

export const getDocumentsValidationSchema = (t, requirements) =>{
    const res = {}
    if(requirements?.vessel_insurance){
        res.vessel_insurance = Yup.object().nullable(true).required(t('DOCUMENT_REQUIRED'))
    }
    if(requirements?.vessel_license){
        res.vessel_license = Yup.object().nullable(true).required(t('DOCUMENT_REQUIRED'))
    }
    if(requirements?.vessel_photo){
        res.vessel_photo = Yup.object().nullable(true).required(t('DOCUMENT_REQUIRED'))
    }
    if(requirements?.skipper_id){
        res.skipper_id = Yup.object().nullable(true).required(t('DOCUMENT_REQUIRED'))
    }
    return res;

}


export const getBeforeApprovalValidationSchema = (t, requirements) => {

    const res = {}
    if(requirements?.vesselName){
        res.vesselName = Yup.string().nullable(true).required(t('VESSEL_NAME_IS_REQUIRED'));
    }
    if(requirements?.emailAddress){
        res.emailAddress = Yup.string().email(t('EMAIL_INCORRECT')).matches(emailRexp, t('EMAIL_INCORRECT'))
        .required(t('EMAIL_IS_REQUIRED'));
    }
    if(requirements?.firstName){
        res.firstName = Yup.string().matches(nameRexp, t('FIRST_NAME_INCORRECT')).required(t('FIRST_NAME_REQUIRED'));
    }
    if(requirements?.lastName){
        res.lastName = Yup.string().matches(nameRexp, t('LAST_NAME_INCORRECT')).required(t('LAST_NAME_REQUIRED'));
    }
    if(requirements?.natureOfTravel) {
        res.natureOfTravel = Yup.string().nullable(true).required(t('NATURE_OF_TRAVEL_REQUIRED'));
    }
    if(requirements?.phone) {
        res.phone = Yup.string().required(t('PHONE_NUMBER_REQUIRED')).matches(phoneRexp, t('PHONE_NUMBER_ERROR'));
    }
    if(requirements?.address) {
        res.address = Yup.string().required(t('ADDRESS_REQUIRED'));
    }
    if(requirements?.postCode) {
        res.postCode = Yup.string().required(t('POSTCODE_REQUIRED'));
    }
    if(requirements?.country) {
        res.country = Yup.string().nullable(true).required(t('COUNTRY_IS_REQUIRED'));
    }
    if(requirements?.vesselType) {
        res.vesselType = Yup.string().nullable(true).required(t('BOAT_TYPE_REQUIRED'));
    }
    if(requirements?.hullType) {
        res.hullType = Yup.string().nullable(true).required(t('HULL_TYPE_REQUIRED'));
    }
    if(requirements?.homeMarina) {
        res.homeMarinaId = Yup.string().nullable(true).required(t('HOME_MARINA_IS_REQUIRED'));
    }
    if(requirements?.natureOfTravel) {
        res.natureOfTravel = Yup.string().nullable(true).required(t('NATURE_OF_TRAVEL_REQUIRED'));
    }
    return res;
}

export const getLoginSchemaObject = t => {
    return Yup.object().shape({
        email: Yup.string()
            .email(t('INVALID_EMAIL'))
            .matches(emailRexp, t('INVALID_EMAIL'))
            .required(t('EMAIL_REQUIRED')),
        password: Yup.string()
            .min(6, t('PASSWORD_LENGTH'))
            .max(20, t('PASSWORD_TOO_LONG'))
            .required(t('PASSWORD_REQUIRED'))
    });
};


export const validateEmail = email => {
    return (new RegExp(emailRexp)).test(email);
}
